/**
 * wayai sync-mcp --connection <name> [--hub <id|name>] [--check] — re-discover an
 * MCP connection's tool/resource catalog, or (with `--check`) only report drift.
 *
 * `wayai push` only discovers MCP tools that are *new* to the hub catalog; it
 * never refreshes the cached input-schema of tools already catalogued. When an
 * external MCP server changes a tool's schema (e.g. adds typed params), the
 * agent keeps calling the stale schema until the catalog is re-synced. This is
 * the explicit, connection-level refresh for that case (gh #2548).
 *
 * `--check` (alias `--dry-run`) is the read-only drift detector (gh #3419): it
 * contacts the server and compares against the cached catalog WITHOUT writing,
 * exiting non-zero when the catalog is behind — so CI can gate on it. It works
 * against production hubs too (read-only); the write sync is preview-only.
 *
 * Preview-only for the write path — production hubs are read-only (changes flow
 * preview → sync).
 */

import { requireAuth } from '../lib/auth.js';
import { ApiClient } from '../lib/api-client.js';
import { requireRepoConfig } from '../lib/repo-config.js';
import { resolveActiveHubId } from '../lib/workspace.js';
import { printWarnings } from './push.js';

export function parseArgs(args: string[]): { connection: string | undefined; check: boolean } {
  let connection: string | undefined;
  let check = false;
  for (let i = 0; i < args.length; i++) {
    const arg = args[i];
    if (arg === '--connection' && args[i + 1]) {
      connection = args[++i];
    } else if (arg === '--check' || arg === '--dry-run') {
      check = true;
    }
  }
  return { connection, check };
}

export async function syncMcpCommand(args: string[]): Promise<void> {
  // resolveActiveHubId parses and removes `--hub <value>` from args.
  const hubId = resolveActiveHubId(args);
  const { connection, check } = parseArgs(args);

  if (!connection) {
    console.error('Usage: wayai sync-mcp --connection <name> [--hub <uuid|folder-name>] [--check]');
    console.error('');
    console.error('Re-discovers the MCP connection\'s tools/resources and refreshes their');
    console.error('cached input schemas. `--connection` accepts the connection display name');
    console.error('(as in connections[].name) or its UUID.');
    console.error('');
    console.error('  --check   Report tool-schema drift (cached catalog vs live server)');
    console.error('            WITHOUT writing anything. Exits 1 when drift is found —');
    console.error('            for CI. Alias: --dry-run.');
    process.exit(1);
  }

  const { organization_id: organizationId } = requireRepoConfig();
  const { config, accessToken } = await requireAuth();
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });

  if (check) {
    console.log(`Checking MCP tool-schema drift for connection "${connection}"...`);
    const result = await client.syncMcp(hubId, connection, organizationId, { check: true });
    const drift = result.drift ?? [];

    if (result.reachable === false) {
      // Couldn't contact the server/credentials — drift is UNVERIFIED, not "in
      // sync". Don't fail CI on a transient outage; make the ambiguity explicit.
      console.log(
        `Could not reach the MCP server to verify drift for "${result.connection_name}"` +
          `${result.message ? ` (${result.message})` : ''}. Catalog drift not verified.`,
      );
      return;
    }

    if (drift.length === 0) {
      console.log(`No drift — connection "${result.connection_name}" catalog matches the server ✓`);
      return;
    }

    const noun = drift.length === 1 ? 'tool has' : 'tools have';
    console.log(`\n⚠ ${drift.length} ${noun} drifted on connection "${result.connection_name}":\n`);
    for (const d of drift) {
      if (d.kind === 'added') {
        console.log(`  + ${d.tool_name} — advertised by the server, not in the catalog`);
      } else if (d.kind === 'removed') {
        console.log(`  - ${d.tool_name} — in the catalog, no longer on the server`);
      } else {
        console.log(`  ~ ${d.tool_name} — ${d.detail ?? 'schema changed'}`);
      }
    }
    console.log(
      // Quote the name — display names are free-form and often contain spaces,
      // and the CLI reads `--connection` as a single token.
      `\nRun \`wayai sync-mcp --connection "${result.connection_name}"\` to refresh the catalog.`,
    );
    process.exit(1);
  }

  console.log(`Syncing MCP tools for connection "${connection}"...`);
  const result = await client.syncMcp(hubId, connection, organizationId);

  console.log(
    result.message ??
      `Synced ${result.tools_count ?? 0} tool(s) and ${result.resources_count ?? 0} resource(s) from MCP server`,
  );

  if (result.warnings && result.warnings.length > 0) {
    printWarnings(result.warnings);
  }
}
